import { MOCK_DEALS } from './mock-data'

/**
 * Mock `deal_activity` event log — one row per logged activity on a deal.
 * Mirrors a future dev.deal_activity table: an append-only event stream we
 * aggregate (counts by type / owner, activity over time) with no backend reads.
 *
 * Deal-level attributes (name, owner) are denormalized onto each row so the
 * frame is self-contained — the prototype never has to join back to the deal.
 * Derived from MOCK_DEALS so deal_id / owner align across the two frames.
 */

/** The activity kinds logged against a deal. */
export type DealActivityEvent =
  | 'deal_created'
  | 'added_note'
  | 'added_contact'
  | 'had_call'
  | 'had_meeting'
  | 'added_doc'
  | 'updated_sales_stage'
  | 'deal_won'
  | 'deal_lost'
  | 'churned'

/** One logged activity event on a deal. */
export type DealActivityRow = {
  id: number // PK, sequential
  company_id: number // tenant viewing the report (hardcoded — single-tenant prototype)
  deal_id: number
  deal_name: string // denormalized from the deal (its company_name)
  owner: string // denormalized account_owner
  owner_id: number // stable id derived from the owner name
  created_at: string // ISO event timestamp
  event_type: DealActivityEvent
}

/** ISO date columns on DealActivityRow — drive date bucketing in groupby. */
export const ACTIVITY_DATEF: string[] = ['created_at']

/** The tenant company viewing the report (single-tenant prototype, hardcoded). */
export const VIEWING_COMPANY_ID = 1

const DAY_MS = 86_400_000

/** Captured once at import for full determinism (open deals' upper time bound). */
const NOW = Date.now()

/** mulberry32 — same tiny seeded PRNG as mock-data, seeded per-deal here. */
const mulberry32 = (seed: number) => () => {
  seed |= 0
  seed = (seed + 0x6d2b79f5) | 0
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

/** Stable owner-name → id map (sorted distinct owners, 1-based). */
const OWNER_IDS: Record<string, number> = (() => {
  const names = [...new Set(MOCK_DEALS.map((d) => d.account_owner))].sort()
  const map: Record<string, number> = {}
  names.forEach((n, i) => (map[n] = i + 1))
  return map
})()

/** Terminal lifecycle event for a closed deal, or null if the deal is still open. */
function terminalEvent(stage: string): DealActivityEvent | null {
  if (stage.startsWith('Closed Won')) return 'deal_won'
  if (stage === 'Closed Lost') return 'deal_lost'
  if (stage === 'Churn') return 'churned'
  return null
}

/**
 * Expand each deal into its activity stream: a `deal_created` marker, a run of
 * engagement events (counts seeded by the deal's own note/contact/stage data so
 * the two frames stay consistent), then a terminal won/lost/churned marker for
 * closed deals — all timestamped within the deal's lifespan.
 */
export function generateActivities(
  deals: typeof MOCK_DEALS = MOCK_DEALS
): DealActivityRow[] {
  const rows: DealActivityRow[] = []
  let id = 1

  for (const d of deals) {
    const rand = mulberry32(d.deal_id)
    const createdMs = Date.parse(d.created)
    const endMs = d.datetime_closed ? Date.parse(d.datetime_closed) : NOW
    const span = Math.max(endMs - createdMs, DAY_MS)

    // Build this deal's events as {event_type, at}, then time-sort before
    // assigning ids so a deal's stream reads chronologically.
    const events: { event_type: DealActivityEvent; at: number }[] = [
      { event_type: 'deal_created', at: createdMs },
    ]

    // Engagement counts: notes/contacts mirror the deal's own counts; the deal
    // moved through `stage_order` stages; calls/meetings/docs are light noise.
    const counts: [DealActivityEvent, number][] = [
      ['added_note', d.note_count],
      ['added_contact', d.contact_count],
      ['updated_sales_stage', d.stage_order],
      ['had_call', Math.floor(rand() * 4)],
      ['had_meeting', Math.floor(rand() * 3)],
      ['added_doc', Math.floor(rand() * 2)],
    ]
    for (const [event_type, n] of counts) {
      for (let k = 0; k < n; k++) {
        events.push({ event_type, at: createdMs + rand() * span })
      }
    }

    // A terminal marker fires only when the deal actually closed (has a close
    // timestamp); won-pending-PO deals are still open, so they get none yet.
    const term = d.datetime_closed ? terminalEvent(d.sales_stage) : null
    if (term) events.push({ event_type: term, at: endMs })

    events.sort((a, b) => a.at - b.at)
    for (const e of events) {
      rows.push({
        id: id++,
        company_id: VIEWING_COMPANY_ID,
        deal_id: d.deal_id,
        deal_name: d.company_name,
        owner: d.account_owner,
        owner_id: OWNER_IDS[d.account_owner],
        created_at: new Date(e.at).toISOString(),
        event_type: e.event_type,
      })
    }
  }
  return rows
}

/** Canonical activity dataset used by the UI. Derived from MOCK_DEALS. */
export const MOCK_DEAL_ACTIVITY: DealActivityRow[] = generateActivities()
